/**
 * OnboardingPage - Collect user details based on mode (individual/couple)
 * Stores data in sessionStorage and forwards to checkout
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ArrowRight, Users, User } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const TIER_LABELS = {
  core: { title: 'Core Clarity', level: 'CORE' },
  ritual: { title: 'Ritual Structure', level: 'RITUAL' },
  legacy: { title: 'Legacy Vision', level: 'LEGACY' },
};

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'couple';
  const tier = searchParams.get('tier') || 'core';
  const tierInfo = TIER_LABELS[tier];
  const isCouple = mode === 'couple';

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    email: '',
    prompt: '',
    partnerFirstName: '',
    partnerEmail: '',
  });

  if (!tierInfo) {
    navigate('/');
    return null;
  }

  const isValid = isCouple
    ? form.firstName.trim() && form.email.trim() && form.partnerFirstName.trim() && form.partnerEmail.trim()
    : form.firstName.trim() && form.email.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;

    setLoading(true);

    // Store onboarding data for downstream pages
    sessionStorage.setItem('onboarding_mode', mode);
    sessionStorage.setItem('onboarding_tier', tier);
    sessionStorage.setItem('onboarding_first_name', form.firstName.trim());
    sessionStorage.setItem('onboarding_email', form.email.trim());

    if (isCouple) {
      sessionStorage.setItem('onboarding_partner_name', form.partnerFirstName.trim());
      sessionStorage.setItem('onboarding_partner_email', form.partnerEmail.trim());
    }
    if (!isCouple && form.prompt.trim()) {
      sessionStorage.setItem('onboarding_prompt', form.prompt.trim());
    }

    toast.success('Details saved! Proceeding to checkout.');
    navigate(`/checkout?level=${tierInfo.level}&mode=${mode}`);
  };

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen" style={{ background: '#F7F5F2' }}>
      <Header showBack onBack={() => navigate(`/start?mode=${mode}&tier=${tier}`)} />

      <div
        className="flex items-center justify-center px-4 sm:px-6 py-10 sm:py-16"
        style={{ minHeight: 'calc(100vh - 100px)' }}
      >
        <div
          className="w-full max-w-lg rounded-2xl p-7 sm:p-10"
          style={{
            background: '#FFFFFF',
            border: '1px solid #E8E5E0',
            boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            {isCouple ? (
              <Users className="w-4 h-4 text-stone-500" />
            ) : (
              <User className="w-4 h-4 text-stone-500" />
            )}
            <span className="text-xs uppercase tracking-widest font-medium text-stone-400">
              {tierInfo.title} &middot; {mode}
            </span>
          </div>

          <h2
            className="text-2xl sm:text-3xl font-bold mb-1"
            style={{ color: '#1A1A1A' }}
            data-testid="onboarding-heading"
          >
            {isCouple ? 'Begin Together' : 'Begin Your Journey'}
          </h2>
          <p className="text-sm text-stone-500 mb-3">
            {isCouple
              ? 'Enter your details and your partner\u2019s to get started.'
              : 'Tell us a little about yourself to get started.'}
          </p>
          <p className="text-xs text-stone-400 mb-8">
            {isCouple
              ? 'Each partner completes their part privately before moving forward together.'
              : 'You can begin with your own clarity now and invite your partner later.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="onboarding-form">
            {/* Your details */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {isCouple ? 'Your first name' : 'First name'} *
              </label>
              <input
                type="text"
                required
                value={form.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                className="w-full h-12 rounded-lg border border-stone-200 bg-stone-50 px-4 text-base focus:border-stone-900 focus:ring-0 transition-all"
                placeholder="e.g. Jordan"
                data-testid="onboarding-first-name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {isCouple ? 'Your email' : 'Email'} *
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                className="w-full h-12 rounded-lg border border-stone-200 bg-stone-50 px-4 text-base focus:border-stone-900 focus:ring-0 transition-all"
                placeholder="you@example.com"
                data-testid="onboarding-email"
              />
            </div>

            {/* Couple-only fields */}
            {isCouple && (
              <>
                <div className="pt-3 border-t border-stone-100">
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Partner first name *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.partnerFirstName}
                    onChange={(e) => update('partnerFirstName', e.target.value)}
                    className="w-full h-12 rounded-lg border border-stone-200 bg-stone-50 px-4 text-base focus:border-stone-900 focus:ring-0 transition-all"
                    placeholder="e.g. Taylor"
                    data-testid="onboarding-partner-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5">
                    Partner email *
                  </label>
                  <input
                    type="email"
                    required
                    value={form.partnerEmail}
                    onChange={(e) => update('partnerEmail', e.target.value)}
                    className="w-full h-12 rounded-lg border border-stone-200 bg-stone-50 px-4 text-base focus:border-stone-900 focus:ring-0 transition-all"
                    placeholder="partner@example.com"
                    data-testid="onboarding-partner-email"
                  />
                </div>

                <p className="text-xs text-stone-400 leading-relaxed">
                  Your partner will receive a guided invitation to complete their part privately.
                </p>
              </>
            )}

            {/* Individual-only prompt */}
            {!isCouple && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  What brings you here today? <span className="text-stone-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={form.prompt}
                  onChange={(e) => update('prompt', e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-base focus:border-stone-900 focus:ring-0 transition-all resize-none"
                  placeholder="I want to understand what I value most in a relationship..."
                  data-testid="onboarding-prompt"
                />
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !isValid}
              className="w-full rounded-full py-3.5 text-base font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: '#2F9ED8', color: '#fff' }}
              data-testid="onboarding-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {isCouple ? 'Begin Our Clarity Journey' : 'Begin My Clarity Journey'}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
